import { useEffect, useRef, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { getStream } from '@/utils/api-handler';
import { ExtendedChatMessage } from './chat-types';
import { mapMessagesAssistantById, markCachedMessagesCompleted } from './message-utils';
import { createEventHandler, processSSEChunk } from './sse-event-handler';

interface UseStreamReconnectArgs {
    agentId: string | null | undefined;
    sessionId: string | null | undefined;
    /** Session-level GENERATING flag (from the session list endpoint). */
    sessionStreamActive: boolean;
    /** Message-level GENERATING id (from the messages list endpoint) — takes precedence over `sessionStreamActive` for targeting. */
    generatingAssistantId: string | null;
    isLoadingMessages: boolean;
    /** Live ref of fetched messages so pagination re-renders don't re-trigger the effect. */
    fetchedMessagesRef: MutableRefObject<ExtendedChatMessage[]>;
    isTypingRef: MutableRefObject<boolean>;
    streamingMessageIdRef: MutableRefObject<string | null>;
    streamCancelledRef: MutableRefObject<boolean>;
    streamAbortRef: MutableRefObject<Set<AbortController>>;
    setMessages: Dispatch<SetStateAction<ExtendedChatMessage[]>>;
    setIsTyping: Dispatch<SetStateAction<boolean>>;
    setStreamingMessageId: Dispatch<SetStateAction<string | null>>;
    setStreamingPhase: Dispatch<SetStateAction<'working' | 'responding'>>;
    onSessionTitle?: (sessionId: string, title: string) => void;
    queryClient: QueryClient;
}

/**
 * Reattaches the SSE stream to an assistant message the server reports is still GENERATING —
 * either via the session's own status flag or a message's `status === 'GENERATING'`. Ensures
 * reload / tab-switch doesn't leave a half-rendered assistant bubble stuck as "thinking".
 */
export function useStreamReconnect({
    agentId,
    sessionId,
    sessionStreamActive,
    generatingAssistantId,
    isLoadingMessages,
    fetchedMessagesRef,
    isTypingRef,
    streamingMessageIdRef,
    streamCancelledRef,
    streamAbortRef,
    setMessages,
    setIsTyping,
    setStreamingMessageId,
    setStreamingPhase,
    onSessionTitle,
    queryClient,
}: UseStreamReconnectArgs) {
    /**
     * Assistant ids we've already finished reconnecting to in this session. Prevents an
     * infinite reconnect loop when the backend leaves `status: 'GENERATING'` on the row
     * after the stream actually ends — without this guard, the effect re-fires every time
     * the messages list refetches and re-resolves the same generating id.
     */
    const handledAssistantIdsRef = useRef<Set<string>>(new Set());
    /**
     * Reset the handled set when the user switches session — a new session has its own
     * generating id, and we want reconnects to fire for it.
     */
    const lastSessionIdRef = useRef<string | null | undefined>(sessionId);
    if (lastSessionIdRef.current !== sessionId) {
        handledAssistantIdsRef.current = new Set();
        lastSessionIdRef.current = sessionId;
    }

    useEffect(() => {
        const streamSessionId = sessionId;
        if (!agentId || !streamSessionId || isTypingRef.current || Boolean(streamingMessageIdRef.current) || isLoadingMessages) return;
        if (!generatingAssistantId) return;
        if (handledAssistantIdsRef.current.has(generatingAssistantId)) return;

        const currentFetchedMessages = fetchedMessagesRef.current;
        if (currentFetchedMessages.length === 0) return;

        // Only reconnect to a message the server actively flags as GENERATING.
        // Falling back to the newest assistant would replay the stream into an already-completed bubble.
        const newestAssistant = currentFetchedMessages.find(
            (m) => m.id === generatingAssistantId && m.role === 'assistant',
        );
        if (!newestAssistant) return;
        if (streamingMessageIdRef.current === newestAssistant.id) return;

        streamCancelledRef.current = false;
        setStreamingMessageId(newestAssistant.id);
        setIsTyping(true);
        setMessages((prev) =>
            mapMessagesAssistantById(prev, newestAssistant.id, (msg) => ({ ...msg, isStreaming: true })),
        );
        setStreamingPhase('working');

        const eventHandler = createEventHandler({
            systemMessageId: newestAssistant.id,
            setMessages,
            onSessionTitle,
            cancelledRef: streamCancelledRef,
            setStreamingPhase,
        });
        const abortController = new AbortController();
        streamAbortRef.current.add(abortController);

        getStream(
            `/api/command-team/agents/${agentId}/sessions/${streamSessionId}/messages/stream`,
            (chunk: string) => processSSEChunk(chunk, eventHandler),
            abortController.signal,
        )
            .then(() => {
                streamAbortRef.current.delete(abortController);
                handledAssistantIdsRef.current.add(newestAssistant.id);
                setIsTyping(false);
                setMessages((prev) =>
                    mapMessagesAssistantById(prev, newestAssistant.id, (msg) => ({
                        ...msg,
                        isStreaming: false,
                        status: 'COMPLETED',
                    })),
                );
                // Flip stale GENERATING in cache locally so the effect doesn't re-resolve
                // the same id. We intentionally do NOT refetch here — the backend can lag
                // and report GENERATING after the stream has ended, which would trigger
                // an infinite reconnect → fetch → reconnect loop.
                markCachedMessagesCompleted(queryClient, agentId, streamSessionId);
                setStreamingMessageId(null);
            })
            .catch((error: any) => {
                streamAbortRef.current.delete(abortController);
                handledAssistantIdsRef.current.add(newestAssistant.id);
                const isAborted =
                    error?.name === 'AbortError' || error?.message?.includes('aborted') || streamCancelledRef.current;
                if (!isAborted) {
                    setIsTyping(false);
                    setStreamingMessageId(null);
                    setMessages((prev) =>
                        mapMessagesAssistantById(prev, newestAssistant.id, (msg) => ({
                            ...msg,
                            isStreaming: false,
                            status: 'COMPLETED',
                        })),
                    );
                }
                // Even on error/abort, mark the id handled so we don't loop on it again.
                markCachedMessagesCompleted(queryClient, agentId, streamSessionId);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingMessages, agentId, sessionId, sessionStreamActive, generatingAssistantId, queryClient]);
}
